export interface ReferralProgram {
  /**
   * Referral program name.
   * @minLength 2
   * @maxLength 50
   */
  name?: string | null;
  /** @readonly */
  status?: ProgramStatusWithLiterals;
  /**
   * Revision number, which increments by 1 each time the program is updated.
   * To prevent conflicting changes, the current `revision` must be passed when updating the program.
   * @immutable
   */
  revision?: string | null;
  /**
   * Date and time the program was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the program was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Reward configuration for the referred friend.
   * Specifies the reward given to a new customer who was referred to the business.
   */
  referredFriendReward?: Reward;
  /**
   * Reward configuration for the referring customer.
   * Specifies the reward given to an existing customer who referred a new customer to the business.
   */
  referringCustomerReward?: Reward;
  /**
   * List of actions that complete a referral. For an action to be considered successful, the referred friend must place and pay for an item.
   * @maxSize 100
   */
  successfulReferralActions?: ActionWithLiterals[];
  /** Configures email notifications for the referral program. */
  emails?: Emails;
  /**
   * Indicates which premium features are available for the current account.
   * @readonly
   */
  premiumFeatures?: PremiumFeatures;
}

/** Status of the referral program. */
export enum ProgramStatus {
  /** Unknown program status. */
  UNKNOWN = 'UNKNOWN',
  /** Referral program is in a draft state and is being modified. It is not yet active. */
  DRAFT = 'DRAFT',
  /** Referral program is active. */
  ACTIVE = 'ACTIVE',
  /** Referral program is paused. */
  PAUSED = 'PAUSED',
}

/** @enumType */
export type ProgramStatusWithLiterals =
  | ProgramStatus
  | 'UNKNOWN'
  | 'DRAFT'
  | 'ACTIVE'
  | 'PAUSED';

export interface Reward extends RewardOptionsOneOf {
  /** Options for coupon reward type. */
  couponOptions?: Coupon;
  /** Options for the Loyalty points reward type. */
  loyaltyPointsOptions?: LoyaltyPoints;
  /** Type of the reward. */
  type?: RewardTypeWithLiterals;
}

/** @oneof */
export interface RewardOptionsOneOf {
  /** Options for coupon reward type. */
  couponOptions?: Coupon;
  /** Options for the Loyalty points reward type. */
  loyaltyPointsOptions?: LoyaltyPoints;
}

export enum RewardType {
  /** Unknown reward type. */
  UNKNOWN = 'UNKNOWN',
  /** Coupon reward type. */
  COUPON = 'COUPON',
  /** Loyalty points reward type. */
  LOYALTY_POINTS = 'LOYALTY_POINTS',
  /** No reward type. */
  NOTHING = 'NOTHING',
}

/** @enumType */
export type RewardTypeWithLiterals =
  | RewardType
  | 'UNKNOWN'
  | 'COUPON'
  | 'LOYALTY_POINTS'
  | 'NOTHING';

export interface Coupon
  extends CouponDiscountTypeOptionsOneOf,
    CouponScopeOrMinSubtotalOneOf {
  /** Options for fixed amount discount. */
  fixedAmountOptions?: FixedAmountDiscount;
  /** Options for percentage discounts. */
  percentageOptions?: PercentageDiscount;
  /** Limit the coupon to carts with a subtotal above this number. */
  minimumSubtotal?: number;
  /** Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values). */
  scope?: CouponScope;
  /**
   * Coupon name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** Coupon discount type. */
  discountType?: DiscountTypeWithLiterals;
  /**
   * Whether the coupon is limited to one item.
   * If `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.
   * Coupons with a bookings `scope.namespace` are always limited to one item.
   */
  limitedToOneItem?: boolean | null;
  /** Whether the coupon applies to subscription products. */
  appliesToSubscriptions?: boolean | null;
  /**
   * Specifies the amount of discounted cycles for a subscription item.
   *
   * - Can only be set when `scope.namespace = pricingPlans`.
   * - If `discountedCycleCount` is empty, the coupon applies to all available cycles.
   * - `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.
   *
   * Max: `999`
   */
  discountedCycleCount?: number | null;
}

/** @oneof */
export interface CouponDiscountTypeOptionsOneOf {
  /** Options for fixed amount discount. */
  fixedAmountOptions?: FixedAmountDiscount;
  /** Options for percentage discounts. */
  percentageOptions?: PercentageDiscount;
}

/** @oneof */
export interface CouponScopeOrMinSubtotalOneOf {
  /** Limit the coupon to carts with a subtotal above this number. */
  minimumSubtotal?: number;
  /** Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values). */
  scope?: CouponScope;
}

export enum DiscountType {
  /** Unknown discount type. */
  UNKNOWN = 'UNKNOWN',
  /** Discount as a fixed amount. */
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  /** Discount as a percentage. */
  PERCENTAGE = 'PERCENTAGE',
  /** Free shipping. If `true`, the coupon applies to all items in all `namespaces`. */
  FREE_SHIPPING = 'FREE_SHIPPING',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'UNKNOWN'
  | 'FIXED_AMOUNT'
  | 'PERCENTAGE'
  | 'FREE_SHIPPING';

export interface FixedAmountDiscount {
  /**
   * Amount of the discount as a fixed value.
   * @min 0.01
   */
  amount?: number;
}

export interface PercentageDiscount {
  /**
   * Percentage of discount.
   * @max 100
   */
  percentage?: number;
}

export interface CouponScope {
  /** Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans) */
  namespace?: string;
  /** Coupon scope's applied group, for example, Event or ticket in Wix Events. */
  group?: Group;
}

export interface Group {
  /** Name of the group. */
  name?: string;
  /** Entity ID of the group. */
  entityId?: string | null;
}

export interface LoyaltyPoints {
  /**
   * Number of loyalty points to give.
   * @min 1
   * @max 9999999
   */
  amount?: number;
}

export enum Action {
  /** Unknown action. */
  UNKNOWN = 'UNKNOWN',
  /** Referred friend ordered and paid for an order in a store. */
  STORE_ORDER_PLACED = 'STORE_ORDER_PLACED',
  /** Referred friend ordered and paid for a plan. */
  PLAN_ORDERED = 'PLAN_ORDERED',
  /** Referred friend ordered and paid for a ticket. */
  TICKET_ORDERED = 'TICKET_ORDERED',
  /** Referred friend booked and paid for a session. */
  SESSION_BOOKED = 'SESSION_BOOKED',
  /** Referred friend placed and paid for a restaurant order. */
  RESTAURANT_ORDER_PLACED = 'RESTAURANT_ORDER_PLACED',
  /** Referred friend joined an online program. */
  ONLINE_PROGRAM_JOINED = 'ONLINE_PROGRAM_JOINED',
}

/** @enumType */
export type ActionWithLiterals =
  | Action
  | 'UNKNOWN'
  | 'STORE_ORDER_PLACED'
  | 'PLAN_ORDERED'
  | 'TICKET_ORDERED'
  | 'SESSION_BOOKED'
  | 'RESTAURANT_ORDER_PLACED'
  | 'ONLINE_PROGRAM_JOINED';

export interface Emails {
  /** Configures email invitations to encourage customers to refer their friends. Select the apps for which this feature is enabled. */
  encourageToReferFriends?: AppWithLiterals[];
  /**
   * Whether to send email notifications to referring customers when they receive a referral reward.
   * If true, referring customers will be notified by email when their referred friend completes a qualifying action (for example, placing an order).
   */
  notifyCustomersAboutReward?: boolean;
}

export enum App {
  /** Unknown app. */
  UNKNOWN = 'UNKNOWN',
  /** Send an email to customers who've placed an order with stores. */
  STORES = 'STORES',
  /** Send an email to customers who've placed an order with pricing plans. */
  PRICING_PLANS = 'PRICING_PLANS',
  /** Send an email to customers who've placed an order with events. */
  EVENTS = 'EVENTS',
  /** Send an email to customers who've placed an order with bookings. */
  BOOKINGS = 'BOOKINGS',
  /** Send an email to customers who've placed an order with restaurants. */
  RESTAURANTS = 'RESTAURANTS',
  /** Send an email to customers who've joined an online program. */
  ONLINE_PROGRAMS = 'ONLINE_PROGRAMS',
}

/** @enumType */
export type AppWithLiterals =
  | App
  | 'UNKNOWN'
  | 'STORES'
  | 'PRICING_PLANS'
  | 'EVENTS'
  | 'BOOKINGS'
  | 'RESTAURANTS'
  | 'ONLINE_PROGRAMS';

export interface PremiumFeatures {
  /**
   * Whether the site owner has access to the referral program feature.
   * @readonly
   */
  referralProgram?: boolean;
}

export interface GetReferralProgramRequest {}

export interface GetReferralProgramResponse {
  /** Retrieved referral program. */
  referralProgram?: ReferralProgram;
}

export interface BulkGetReferralProgramRequest {}

export interface BulkGetReferralProgramResponse {
  /** Retrieved referral programs. */
  programInSites?: ProgramInSite[];
}

export interface ProgramInSite {
  /**
   * Metasite ID.
   * @format GUID
   */
  metaSiteId?: string;
  /** Retrieved referral program. */
  referralProgram?: ReferralProgram;
}

export interface UpdateReferralProgramRequest {
  /** Referral program to update. Include the latest `revision` for a successful update. */
  referralProgram: ReferralProgram;
}

export interface UpdateReferralProgramResponse {
  /** Updated referral program. */
  referralProgram?: ReferralProgram;
}

export interface ActivateReferralProgramRequest {}

export interface ActivateReferralProgramResponse {
  /** Activated referral program. */
  referralProgram?: ReferralProgram;
}

export interface PauseReferralProgramRequest {}

export interface PauseReferralProgramResponse {
  /** Paused referral program. */
  referralProgram?: ReferralProgram;
}

export interface GetAISocialMediaPostsSuggestionsRequest {
  /**
   * Topic to generate social media post suggestions for. For example, fitness, education, or technology.
   * @maxLength 512
   */
  topic?: string;
}

export interface GetAISocialMediaPostsSuggestionsResponse {
  /**
   * Generated social media post suggestions.
   * @maxSize 3
   */
  suggestions?: AISocialMediaPostSuggestion[];
  /**
   * Referral URL to refer friends.
   * @maxLength 2083
   */
  referFriendsPageUrl?: string | null;
}

export interface AISocialMediaPostSuggestion {
  /**
   * Suggested post content.
   * @maxLength 4096
   */
  postContent?: string;
  /**
   * Suggested hashtags.
   * @maxLength 256
   * @maxSize 256
   */
  hashtags?: string[];
}

export interface GenerateAISocialMediaPostsSuggestionsRequest {
  /**
   * Topic to generate social media post suggestions for. For example, fitness, education, or technology.
   * @maxLength 512
   */
  topic?: string;
}

export interface GenerateAISocialMediaPostsSuggestionsResponse {
  /**
   * Generated social media post suggestions.
   * @maxSize 3
   */
  suggestions?: AISocialMediaPostSuggestion[];
  /**
   * Referral URL to refer friends.
   * @maxLength 2083
   */
  referFriendsPageUrl?: string | null;
}

export interface GetReferralProgramPremiumFeaturesRequest {}

export interface GetReferralProgramPremiumFeaturesResponse {
  /**
   * Whether the site has the referral program feature enabled.
   * @readonly
   */
  referralProgram?: boolean;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface HtmlSitePublished {
  /**
   * Application instance ID
   * @maxLength 50
   */
  appInstanceId?: string;
  /**
   * Application type
   * @maxLength 100
   */
  appType?: string;
  /** Revision */
  revision?: string;
  /**
   * MSID
   * @maxLength 100
   */
  metaSiteId?: string | null;
  /**
   * optional branch id if publish is done from branch
   * @format GUID
   */
  branchId?: string | null;
  /** The site's last transactionId */
  lastTransactionId?: string | null;
  /** A list of the site's pages */
  pages?: Page[];
  /** Site's publish date */
  publishDate?: string;
  /** Determine if this message was produced by editor-deployments service */
  originatedFromDeploymentsService?: boolean;
}

export interface Page {
  /**
   * Page's Id
   * @maxLength 100
   */
  id?: string;
}

export interface SubscriptionEvent extends SubscriptionEventEventOneOf {
  /** Triggered when a subscription is created. */
  created?: SubscriptionCreated;
  /**
   * Triggered when a subscription is assigned to a Wix site, including the initial
   * assignment of a floating subscription or a re-assignement from a different site.
   */
  assigned?: SubscriptionAssigned;
  /** Triggered when a subscription is canceled. */
  cancelled?: SubscriptionCancelled;
  /** Triggered when the subscription's auto renew is turned on. */
  autoRenewTurnedOn?: SubscriptionAutoRenewTurnedOn;
  /** Triggered when the subscription's auto renew is turned off. */
  autoRenewTurnedOff?: SubscriptionAutoRenewTurnedOff;
  /**
   * Triggered when a subscription is unassigned from a Wix site and becomes
   * floating.
   */
  unassigned?: SubscriptionUnassigned;
  /**
   * Triggered when a subscription is transferred from one Wix account to another.
   * A transfer includes cancelling the original subscription and creating a new
   * subscription for the target account. The event returns both the original
   * and the new subscription.
   */
  transferred?: SubscriptionTransferred;
  /** Triggered when a recurring charge succeeds for a subscription. */
  recurringChargeSucceeded?: RecurringChargeSucceeded;
  /**
   * Triggered when a subscription was updated including when its product has been
   * up- or downgraded or the billing cycle is changed.
   */
  contractSwitched?: ContractSwitched;
  /**
   * Triggered when a subscription gets close to the end of its billing cycle.
   * The exact number of days is defined in the billing system.
   */
  nearEndOfPeriod?: SubscriptionNearEndOfPeriod;
  /**
   * Triggered when a subscription is updated and the change doesn't happen
   * immediately but at the end of the current billing cycle.
   */
  pendingChange?: SubscriptionPendingChange;
  /** Triggered when a recurring charge attempt fails for a subscription. */
  recurringChargeAttemptFailed?: RecurringChargeAttemptFailed;
  /**
   * ID of the subscription's event.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Date and time of the event in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  eventDate?: Date | null;
}

/** @oneof */
export interface SubscriptionEventEventOneOf {
  /** Triggered when a subscription is created. */
  created?: SubscriptionCreated;
  /**
   * Triggered when a subscription is assigned to a Wix site, including the initial
   * assignment of a floating subscription or a re-assignement from a different site.
   */
  assigned?: SubscriptionAssigned;
  /** Triggered when a subscription is canceled. */
  cancelled?: SubscriptionCancelled;
  /** Triggered when the subscription's auto renew is turned on. */
  autoRenewTurnedOn?: SubscriptionAutoRenewTurnedOn;
  /** Triggered when the subscription's auto renew is turned off. */
  autoRenewTurnedOff?: SubscriptionAutoRenewTurnedOff;
  /**
   * Triggered when a subscription is unassigned from a Wix site and becomes
   * floating.
   */
  unassigned?: SubscriptionUnassigned;
  /**
   * Triggered when a subscription is transferred from one Wix account to another.
   * A transfer includes cancelling the original subscription and creating a new
   * subscription for the target account. The event returns both the original
   * and the new subscription.
   */
  transferred?: SubscriptionTransferred;
  /** Triggered when a recurring charge succeeds for a subscription. */
  recurringChargeSucceeded?: RecurringChargeSucceeded;
  /**
   * Triggered when a subscription was updated including when its product has been
   * up- or downgraded or the billing cycle is changed.
   */
  contractSwitched?: ContractSwitched;
  /**
   * Triggered when a subscription gets close to the end of its billing cycle.
   * The exact number of days is defined in the billing system.
   */
  nearEndOfPeriod?: SubscriptionNearEndOfPeriod;
  /**
   * Triggered when a subscription is updated and the change doesn't happen
   * immediately but at the end of the current billing cycle.
   */
  pendingChange?: SubscriptionPendingChange;
  /** Triggered when a recurring charge attempt fails for a subscription. */
  recurringChargeAttemptFailed?: RecurringChargeAttemptFailed;
}

/** Triggered when a subscription is created. */
export interface SubscriptionCreated {
  /** Created subscription. */
  subscription?: Subscription;
  /** Metadata for the `created` event. */
  metadata?: Record<string, string>;
  /**
   * Subscription reactivation data.
   * A subscription can be reactivated for example if it was incorrectly canceled because of fraud and then reactivated
   * by the billing system
   */
  reactivationData?: ReactivationData;
}

/**
 * A subscription holds information about a Premium product that a Wix account
 * owner has purchased including details about the billing.
 */
export interface Subscription {
  /**
   * ID of the subscription.
   * @format GUID
   */
  id?: string;
  /**
   * ID of the Wix account that purchased the subscription.
   * @format GUID
   */
  userId?: string;
  /**
   * ID of the [product](https://bo.wix.com/wix-docs/rest/premium/premium-product-catalog-v2/products/product-object)
   * for which the subscription was purchased.
   * @format GUID
   */
  productId?: string;
  /**
   * Date and time the subscription was created in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  createdAt?: Date | null;
  /**
   * Date and time the subscription was last updated in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  updatedAt?: Date | null;
  /**
   * ID of the metasite that the subscription is assigned to.
   * Available only when the subscription is assigned to a Wix site.
   * Subscriptions for account level products can't be assigned to a Wix site.
   * @format GUID
   */
  metaSiteId?: string | null;
  /** Information about the system that manages the subscription's billing. */
  billingReference?: BillingReference;
  /** Information about the billing cycle of the subscription. */
  cycle?: Cycle;
  /**
   * Subscription status.
   *
   * + `UNKNOWN`: Default status.
   * + `AUTO_RENEW_ON`: Subscription is active and automatically renews at the end of the current billing cycle.
   * + `AUTO_RENEW_OFF`: Subscription is active but expires at the end of the current billing cycle.
   * + `MANUAL_RECURRING`: Subscription is active and renews at the end of the current billing cycle, in case the customer takes an action related to the payment.
   * + `CANCELLED`: Subscription isn't active because it has been canceled.
   * + `TRANSFERRED`: Subscription isn't active because it has been transferred to a different account. A different active subscription was created for the target account.
   */
  status?: SubscriptionStatusWithLiterals;
  /**
   * Date and time the subscription was last transferred from one Wix account to
   * another in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  transferredAt?: Date | null;
  /**
   * ID of the [product type](https://bo.wix.com/wix-docs/rest/premium/premium-product-catalog-v2/product-types/product-type-object)
   * that the product, for which the subscription was purchased, belongs to.
   * @format GUID
   */
  productTypeId?: string;
  /** Version number, which increments by 1 each time the subscription is updated. */
  version?: number;
  /**
   * Whether the subscription is active. Includes the statuses
   * `"AUTO_RENEW_ON"`, `"AUTO_RENEW_OFF"`, and `"MANUAL_RECURRING"`.
   */
  active?: boolean;
  /**
   * Date and time the subscription was originally created in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * Differs from `createdAt` in case the subscription was originally created for a different Wix account and has been transferred.
   */
  originalCreationDate?: Date | null;
  /** Custom metadata about the subscription. */
  metadata?: Record<string, string>;
  /**
   * 2-letter country code in
   * [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements)
   * format.
   */
  countryCode?: string | null;
  /**
   * Seats of the product that the subscription was purchased for.
   * Will be Null for products that don't support seats.
   * @min 1
   * @max 500
   */
  seats?: number | null;
}

export interface BillingReference {
  /**
   * Name of the billing system that manages the subscription.
   *
   * + `"UNKNOWN"`: Default value.
   * + `"SBS"`: [Wix Billing](https://github.com/wix-p/premium-billing/tree/master/sbs).
   * + `"LICENSER"`:
   * + `"BASS"`: [Billing and Subscriptions System](https://dev.wix.com/docs/rest/internal-only/premium/subscriptions-by-billing-by-wix/introduction).
   * + `"RESELLER"`: [External Reseller](https://dev.wix.com/api/rest/account-level-apis/resellers/introduction).
   */
  providerName?: ProviderNameWithLiterals;
  /** Current provider reference ID. */
  providerReferenceId?: string | null;
  /** Previous provider reference IDs. Used for when a subscription is extended, specifically for domains. */
  previousProviderReferenceIds?: string[];
}

export enum ProviderName {
  UNKNOWN = 'UNKNOWN',
  SBS = 'SBS',
  LICENSER = 'LICENSER',
  BASS = 'BASS',
  RESELLER = 'RESELLER',
  RECURRING_INVOICES = 'RECURRING_INVOICES',
}

/** @enumType */
export type ProviderNameWithLiterals =
  | ProviderName
  | 'UNKNOWN'
  | 'SBS'
  | 'LICENSER'
  | 'BASS'
  | 'RESELLER'
  | 'RECURRING_INVOICES';

export interface Cycle extends CycleCycleSelectorOneOf {
  /** repetitive interval */
  interval?: Interval;
  /** one time */
  oneTime?: OneTime;
}

/** @oneof */
export interface CycleCycleSelectorOneOf {
  /** repetitive interval */
  interval?: Interval;
  /** one time */
  oneTime?: OneTime;
}

export interface Interval {
  /** interval unit of measure */
  unit?: IntervalUnitWithLiterals;
  /** number of interval */
  count?: number;
}

export enum IntervalUnit {
  /** unknown interval unit */
  UNKNOWN = 'UNKNOWN',
  /** day */
  DAY = 'DAY',
  /** week */
  WEEK = 'WEEK',
  /** month */
  MONTH = 'MONTH',
  /** year */
  YEAR = 'YEAR',
}

/** @enumType */
export type IntervalUnitWithLiterals =
  | IntervalUnit
  | 'UNKNOWN'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface OneTime {}

export enum SubscriptionStatus {
  UNKNOWN = 'UNKNOWN',
  AUTO_RENEW_ON = 'AUTO_RENEW_ON',
  AUTO_RENEW_OFF = 'AUTO_RENEW_OFF',
  MANUAL_RECURRING = 'MANUAL_RECURRING',
  CANCELLED = 'CANCELLED',
  TRANSFERRED = 'TRANSFERRED',
}

/** @enumType */
export type SubscriptionStatusWithLiterals =
  | SubscriptionStatus
  | 'UNKNOWN'
  | 'AUTO_RENEW_ON'
  | 'AUTO_RENEW_OFF'
  | 'MANUAL_RECURRING'
  | 'CANCELLED'
  | 'TRANSFERRED';

/** Triggered when a subscription is reactivated. */
export interface ReactivationData {
  reactivationReason?: ReactivationReasonEnumWithLiterals;
  /**
   * In the event of reactivation after chargeback dispute, the subscription may be extended according to the
   * number of days it was inactive during the time of resolving the dispute
   */
  newEndOfPeriod?: Date | null;
  /** The original end date, before the inactive period. */
  oldEndOfPeriod?: Date | null;
  /** The difference in days between the new new_end_of_period and old_end_of_period */
  differenceInDays?: number | null;
}

/** Reason for subscription reactivation */
export enum ReactivationReasonEnum {
  UNKNOWN = 'UNKNOWN',
  /**
   * Subscription was reactivated due to billing status change from CANCELED to ACTIVE, for example if it was incorrectly
   * canceled because of suspicion of fraud
   */
  BILLING_STATUS_CHANGE = 'BILLING_STATUS_CHANGE',
  /** Subscription was reactivated after a chargeback dispute */
  REACTIVATED_AFTER_CHARGEBACK = 'REACTIVATED_AFTER_CHARGEBACK',
}

/** @enumType */
export type ReactivationReasonEnumWithLiterals =
  | ReactivationReasonEnum
  | 'UNKNOWN'
  | 'BILLING_STATUS_CHANGE'
  | 'REACTIVATED_AFTER_CHARGEBACK';

/**
 * Triggered when a subscription is assigned to a Wix site, including the initial
 * assignment of a floating subscription or a re-assignement from a different site.
 */
export interface SubscriptionAssigned {
  /** Assigned subscription. */
  subscription?: Subscription;
  /**
   * ID of the metasite that the subscription has been assigned to before the update.
   * @format GUID
   */
  previousMetaSiteId?: string | null;
}

/** Triggered when a subscription is canceled. */
export interface SubscriptionCancelled {
  /** Canceled subscription. */
  subscription?: Subscription;
  /** Details about the cancellation including who canceled the subscription and why. */
  cancellationDetails?: CancellationDetails;
  /**
   * Whether the subscription is canceled immediately or expires at the end of the current billing cycle.
   *
   * Default: `false`
   */
  immediateCancel?: boolean;
  /** Whether the subscription was canceled during the free trial period. */
  canceledInFreeTrial?: boolean;
  /** The type of refund applied to the cancellation. */
  refundType?: RefundTypeWithLiterals;
}

/** Information about the cancellation flow including who canceled the subscription and why it was canceled. */
export interface CancellationDetails {
  /**
   * Cancellation code.
   *
   * Values supported for cancellations on behalf of the billing system: `-1`, `-2`, `-3`, `-4`, `-5`, `-6`, `-7`, `-8`.
   * For cancellations on behalf of the site owner or the service provider `cancellationCode`
   * is taken from the request of
   * [Cancel Immediately Offline](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/cancel-immediately-offline).
   *
   * + `-1`: The subscription has been cancelled by the billing system but none of the listed cancellation reasons applies.
   * + `-2`: There were payment problems.
   * + `-3`: There was a chargeback.
   * + `-4`: Customer support has canceled the subscription and issued a refund.
   * + `-5`: The site owner has changed their existing subscription.
   * + `-6`: The subscription has been transferred to a different Wix account.
   * + `-7`: The subscription has been canceled because the site owner hasn't manually authenticated the recurring payment during the subscription's grace period. For example, site owners must manually confirm recurring payments within 40 days when paying with boleto.
   * + `-8`: The Wix account that the subscription belonged to has been deleted.
   */
  cancellationCode?: number | null;
  /**
   * Cancellation reason. For cancellations on behalf of the site owner or the service provider `cancellationReason`
   * is taken from the request of
   * [Cancel Immediately Offline](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/cancel-immediately-offline).
   * For cancellations on behalf of the billing system `cancellationReason` is `null` or an empty string.
   */
  cancellationReason?: string | null;
  /**
   * Initiator of the cancellation. For `"USER_REQUESTED"` and `"APP_MANAGED"`,
   * `cancellationCode` and `cancellationReason` are taken from the request of
   * [Cancel Immediately](https://dev.wix.com/docs/rest/account-level/premium/premium-subscriptions-manager/subscription-v1/cancel-immediately)
   * or [Cancel Immediately Offline](https://dev.wix.com/docs/rest/account-level/premium/premium-subscriptions-manager/subscription-v1/cancel-immediately-offline).
   * For `"PASSIVE"`, cancellations `cancellationCode` is automatically calculated and `cancellationReason`
   * is `null` or an empty string.
   *
   * + `"UNKNOWN`: Default value.
   * + `"USER_REQUESTED"`:  The Wix account owner has canceled the subscription.
   * + `"APP_MANAGED"`: The service provider has canceled the subscription.
   * + `"PASSIVE"`: The billing system has canceled the subscription. For example, in case of payment failure or fraud.
   */
  initiator?: InitiatorWithLiterals;
}

export enum Initiator {
  UNKNOWN = 'UNKNOWN',
  USER_REQUESTED = 'USER_REQUESTED',
  APP_MANAGED = 'APP_MANAGED',
  PASSIVE = 'PASSIVE',
}

/** @enumType */
export type InitiatorWithLiterals =
  | Initiator
  | 'UNKNOWN'
  | 'USER_REQUESTED'
  | 'APP_MANAGED'
  | 'PASSIVE';

export enum RefundType {
  UNKNOWN = 'UNKNOWN',
  NO_REFUND = 'NO_REFUND',
  FULL_REFUND = 'FULL_REFUND',
  PRORATED_REFUND = 'PRORATED_REFUND',
}

/** @enumType */
export type RefundTypeWithLiterals =
  | RefundType
  | 'UNKNOWN'
  | 'NO_REFUND'
  | 'FULL_REFUND'
  | 'PRORATED_REFUND';

/** Triggered when the subscription's auto renew is turned on. */
export interface SubscriptionAutoRenewTurnedOn {
  /** Subscription for which auto renew is turned on. */
  subscription?: Subscription;
  /**
   * Supported values: `USER`, `APP`.
   *
   * Information about who turned auto renew on.
   * + `"USER"`: The site owner who purchased the subscription has turned auto renew on.
   * + `"APP"`: The service provider has turned auto renew on.
   */
  initiator?: string | null;
}

/** Triggered when the subscription's auto renew is turned off. */
export interface SubscriptionAutoRenewTurnedOff {
  /** Subscription for which auto renew is turned off. */
  subscription?: Subscription;
  /** Details about the cancellation including who canceled the subscription and why. */
  cancellationDetails?: CancellationDetails;
  /**
   * Whether the subscription is immediately canceled or expires at the end of the current billing cycle.
   *
   * Default: `false`
   */
  immediateCancel?: boolean;
}

/**
 * Triggered when a subscription is unassigned from a Wix site and becomes
 * floating.
 */
export interface SubscriptionUnassigned {
  /** Unassigned subscription. */
  subscription?: Subscription;
  /**
   * ID of the metasite that the subscription has been assigned to before the event.
   * @format GUID
   */
  previousMetaSiteId?: string;
  /**
   * Reason why the subscription is unassigned.
   *
   * + `"UNKNOWN"`: Default value.
   * + `"USER_REQUESTED"`: The Wix account owner has unassigned the subscription.
   * + `"REPLACED_BY_ANOTHER_SUBSCRIPTION"`: A different subscription that replaces this subscription is assigned to the site.
   */
  unassignReason?: UnassignReasonWithLiterals;
}

export enum UnassignReason {
  UNKNOWN = 'UNKNOWN',
  USER_REQUESTED = 'USER_REQUESTED',
  REPLACED_BY_ANOTHER_SUBSCRIPTION = 'REPLACED_BY_ANOTHER_SUBSCRIPTION',
}

/** @enumType */
export type UnassignReasonWithLiterals =
  | UnassignReason
  | 'UNKNOWN'
  | 'USER_REQUESTED'
  | 'REPLACED_BY_ANOTHER_SUBSCRIPTION';

/**
 * Triggered when a subscription is transferred from one Wix account to another.
 * A transfer includes cancelling the original subscription and creating a new
 * subscription for the target account. The event returns both the original
 * and the new subscription.
 */
export interface SubscriptionTransferred {
  /** Original subscription that was canceled for the transfer. */
  originSubscription?: Subscription;
  /** Newly created subscription for the target account. */
  targetSubscription?: Subscription;
}

/** Triggered when a recurring charge succeeds for a subscription. */
export interface RecurringChargeSucceeded {
  /** Subscription for which the recurring charge has succeeded. */
  subscription?: Subscription;
  /** Indication that there was a successful charge at the end of the free trial period */
  freeTrialPeriodEnd?: boolean;
}

/**
 * Triggered when a subscription was updated including when its product has been
 * up- or downgraded or the billing cycle is changed.
 */
export interface ContractSwitched {
  /** Updated subscription. */
  subscription?: Subscription;
  /** Billing cycle before the update. */
  previousCycle?: Cycle;
  /**
   * ID of the product belonging to the subscription before the update.
   * @format GUID
   */
  previousProductId?: string;
  /**
   * ID of the product type that the subscription's original product belonged to before the update.
   * @format GUID
   */
  previousProductTypeId?: string;
  /**
   * Update type. __Note__: Doesn't include information about a product adjustment.
   * For that purpose, see `productAdjustment`.
   *
   * + `"NOT_APPLICABLE"`: Default value.
   * + `"ADDITIONAL_QUANTITY"`: An increased usage quota is added to the subscription. For example, a second mailbox is added to a subscription that previously included a single mailbox.
   * + `"CREDIT_UNUSED_PERIOD"`: The subscription is upgraded and the new price is less than the regular price. The new price applies to every billing cycle, not just the first cycle.
   * + `"REFUND_PRICE_DIFF"`: Not implemented.
   * + `"ADJUST_PERIOD_END"`: Not implemented.
   * + `"DOWNGRADE_GRACE_PERIOD"`: For downgrades during the grace period. In this situation, the site owner hasn’t paid yet and must immediately pay for the downgraded subscription.
   * + `"FULL_AMOUNT_PERIOD"`: For upgrades in which the site owner retains unused benefits. For example, site owners upgrading a Facebook Ads subscription retain their unused FB Ads credit. The unused credit is added to the new credit.
   * + `"END_OF_PERIOD"`: The subscription's billing current cycle is extended because of a downgrade.
   * + `"PENDING_CHANGES"`: The subscription's billing is updated, but the change doesn't apply immediately. Instead, the update becomes effective at the end of current billing cycle.
   * + `"DOWNGRADE_RENEWAL"`: The subscription is downgraded because of a declined payment. This prevents subscriptions from churning.
   */
  contractSwitchType?: ContractSwitchTypeWithLiterals;
  /**
   * ID of the metasite the subscription has been assigned to previously.
   * Available only in case the subscription is assigned to a different site.
   * @format GUID
   */
  previousMetaSiteId?: string | null;
  /**
   * Update reason.
   *
   * + `"PRICE_INCREASE"`: The subscription's price has been increased.
   * + `"EXTERNAL_PROVIDER_TRIGGER"`: Any reason other than a price increase.
   */
  contractSwitchReason?: ContractSwitchReasonWithLiterals;
  /** Information about the price update. Available only for updates with a price increase. */
  productPriceIncreaseData?: ProductPriceIncreaseData;
  /**
   * Information about a product adjustment. For example, a downgrade.
   * __Note__: This isn't the same as `contractSwitchType`.
   *
   * + `NOT_APPLICABLE`: There is no information about whether the product has been up- or downgraded.
   * + `DOWNGRADE`: The product has been downgraded.
   */
  productAdjustment?: ProductAdjustmentWithLiterals;
  /**
   * Number of seats before the contract switch.
   * @min 1
   * @max 500
   */
  previousSeats?: number | null;
}

/** Copied from SBS */
export enum ContractSwitchType {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  ADDITIONAL_QUANTITY = 'ADDITIONAL_QUANTITY',
  CREDIT_UNUSED_PERIOD = 'CREDIT_UNUSED_PERIOD',
  REFUND_PRICE_DIFF = 'REFUND_PRICE_DIFF',
  ADJUST_PERIOD_END = 'ADJUST_PERIOD_END',
  DOWNGRADE_GRACE_PERIOD = 'DOWNGRADE_GRACE_PERIOD',
  FULL_AMOUNT_PERIOD = 'FULL_AMOUNT_PERIOD',
  END_OF_PERIOD = 'END_OF_PERIOD',
  PENDING_CHANGES = 'PENDING_CHANGES',
  DOWNGRADE_RENEWAL = 'DOWNGRADE_RENEWAL',
  FIXED_BILLING_DATE_PRORATION = 'FIXED_BILLING_DATE_PRORATION',
  IMMEDIATE_SWITCH = 'IMMEDIATE_SWITCH',
}

/** @enumType */
export type ContractSwitchTypeWithLiterals =
  | ContractSwitchType
  | 'NOT_APPLICABLE'
  | 'ADDITIONAL_QUANTITY'
  | 'CREDIT_UNUSED_PERIOD'
  | 'REFUND_PRICE_DIFF'
  | 'ADJUST_PERIOD_END'
  | 'DOWNGRADE_GRACE_PERIOD'
  | 'FULL_AMOUNT_PERIOD'
  | 'END_OF_PERIOD'
  | 'PENDING_CHANGES'
  | 'DOWNGRADE_RENEWAL'
  | 'FIXED_BILLING_DATE_PRORATION'
  | 'IMMEDIATE_SWITCH';

export enum ContractSwitchReason {
  EXTERNAL_PROVIDER_TRIGGER = 'EXTERNAL_PROVIDER_TRIGGER',
  PRICE_INCREASE = 'PRICE_INCREASE',
}

/** @enumType */
export type ContractSwitchReasonWithLiterals =
  | ContractSwitchReason
  | 'EXTERNAL_PROVIDER_TRIGGER'
  | 'PRICE_INCREASE';

/** Triggered when a subscription's price is increased. */
export interface ProductPriceIncreaseData {
  /**
   * Price of the subscription before the update.
   * @format DECIMAL_VALUE
   */
  previousPrice?: string | null;
  /** A value that is used in order to select the correct email template to send the user regarding the price increase. */
  emailTemplateSelector?: string | null;
  /** Used to differentiate between migration segments. Does not have to be unique per segment. */
  segmentName?: string | null;
  /** Used to determine how the price increase was triggered. */
  priceIncreaseTrigger?: PriceIncreaseTriggerWithLiterals;
}

/** Reason for Price Increase Trigger */
export enum PriceIncreaseTrigger {
  NEAR_RENEWAL = 'NEAR_RENEWAL',
  RECURRING_SUCCESS = 'RECURRING_SUCCESS',
  MANUAL = 'MANUAL',
}

/** @enumType */
export type PriceIncreaseTriggerWithLiterals =
  | PriceIncreaseTrigger
  | 'NEAR_RENEWAL'
  | 'RECURRING_SUCCESS'
  | 'MANUAL';

/** Triggered when a subscription's product is adusted. */
export enum ProductAdjustment {
  /** flag to show that the ContractSwitchedEvent is not applicable / needed */
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  /** flag to show that the ContractSwitchedEvent is a Downgrade */
  DOWNGRADE = 'DOWNGRADE',
}

/** @enumType */
export type ProductAdjustmentWithLiterals =
  | ProductAdjustment
  | 'NOT_APPLICABLE'
  | 'DOWNGRADE';

/**
 * Triggered when a subscription gets close to the end of its billing cycle.
 * The exact number of days is defined in the billing system.
 */
export interface SubscriptionNearEndOfPeriod {
  /** Subscription that got close to the end of its billing cycle. */
  subscription?: Subscription;
  /** Whether the subscription is within the free trial period. */
  inFreeTrial?: boolean;
}

/**
 * Triggered when a subscription is updated and the change doesn't happen
 * immediately but at the end of the current billing cycle.
 */
export interface SubscriptionPendingChange {
  /** Subscription for which a pending update is triggered. */
  subscription?: Subscription;
}

/** Triggered when a recurring charge attempt failed for a subscription. */
export interface RecurringChargeAttemptFailed {
  /** Subscription for which the recurring charge attempt has failed. */
  subscription?: Subscription;
}

export interface ReferralAppInstallation {
  type?: TypeWithLiterals;
}

export enum Type {
  UNSPECIFIED = 'UNSPECIFIED',
  INSTALLED = 'INSTALLED',
  DELETED = 'DELETED',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNSPECIFIED' | 'INSTALLED' | 'DELETED';

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
